import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, User, Phone, ArrowRight, AlertCircle, CheckCircle2, Building, ArrowLeft } from 'lucide-react';
import useAuthStore from '@/store/authStore';
import { authAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import AuthLayout from '@/components/auth/AuthLayout';

// Liste des domaines d'activité pour les clients
const ACTIVITY_DOMAINS = [
  'Transport et logistique',
  'Commerce général',
  'Import/Export',
  'Agriculture',
  'Construction',
  'Industrie manufacturière',
  'Services financiers',
  'Technologie et IT',
  'Santé et médical',
  'Éducation et formation',
  'Hôtellerie et restauration',
  'Immobilier',
  'Énergie',
  'Télécommunications',
  'Commerce de détail',
];

// Liste des domaines de transport pour les transporteurs
const TRANSPORT_DOMAINS = [
  'Transport de marchandises lourdes',
  'Transport de colis légers',
  'Transport de personnes',
  'Service de messagerie/express',
  'Transport frigorifique',
  'Transport de matières dangereuses',
  'Transport de matériaux de construction',
  'Transport agricole',
  'Transport minier',
  'Transport de carburant',
  'Transport de déchets',
  'Déménagement/Transport de meubles',
];

/** Aligné sur core.utils.phone + OTP_VERIFIED_TTL (10 min côté serveur). */
const DEFAULT_PHONE_CC = '+224';
const PHONE_VERIFIED_STORAGE_KEY = 'lc_phone_verification_v1';
const PHONE_VERIFIED_TTL_MS = 10 * 60 * 1000;

function normalizePhoneRaw(phone) {
  return (phone || '').replace(/[^\d+]/g, '').trim();
}

function ensureCountryCode(phone, defaultCC = DEFAULT_PHONE_CC) {
  if (!phone) return '';
  if (phone.startsWith('+')) return phone;
  let prefix = defaultCC.replace(/[^\d+]/g, '');
  if (!prefix.startsWith('+')) prefix = `+${prefix}`;
  return prefix + phone;
}

function normalizePhoneForStorage(phone) {
  const n = normalizePhoneRaw(phone);
  if (!n) return '';
  return ensureCountryCode(n);
}

function loadPhoneVerificationFromStorage() {
  try {
    const raw = localStorage.getItem(PHONE_VERIFIED_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (
      !data
      || typeof data.phoneNormalized !== 'string'
      || typeof data.expiresAt !== 'number'
    ) {
      return null;
    }
    if (Date.now() > data.expiresAt) {
      localStorage.removeItem(PHONE_VERIFIED_STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function savePhoneVerificationToStorage(phoneNormalized) {
  const expiresAt = Date.now() + PHONE_VERIFIED_TTL_MS;
  localStorage.setItem(
    PHONE_VERIFIED_STORAGE_KEY,
    JSON.stringify({ phoneNormalized, expiresAt }),
  );
}

function clearPhoneVerificationStorage() {
  try {
    localStorage.removeItem(PHONE_VERIFIED_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuthStore();

  // Déterminer l'onglet actif depuis l'URL ou par défaut "login"
  const activeTab = searchParams.get('tab') || 'login';
  const [currentTab, setCurrentTab] = useState(activeTab);

  // Vérifier si la session a expiré
  useEffect(() => {
    const sessionExpired = searchParams.get('session_expired') === 'true';
    const sessionExpiredMessage = sessionStorage.getItem('sessionExpiredMessage');

    if (sessionExpired || sessionExpiredMessage) {
      // Afficher le message informatif
      toast({
        title: 'Déconnexion automatique',
        description: 'Vous avez été déconnecté. Veuillez vous reconnecter pour continuer.',
        variant: 'default',
      });

      // Nettoyer le flag et le paramètre URL
      sessionStorage.removeItem('sessionExpiredMessage');
      if (sessionExpired) {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('session_expired');
          return newParams;
        });
      }
    }
  }, [searchParams, setSearchParams]);

  // État pour le formulaire de connexion (email ou téléphone + mot de passe)
  const [loginData, setLoginData] = useState({
    email_or_phone: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [loginErrors, setLoginErrors] = useState({});

  // État pour le formulaire d'inscription
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    role: 'CLIENT',
    type_client: '',
    company_name: '',
    domaine_activites: [],
    address: '',
    is_company: false,
    organisation_name: '',
    registration_number: '',
    tax_info: '',
    transport_domains: [],
  });
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isRegisterLoading, setIsRegisterLoading] = useState(false);
  const [registerErrors, setRegisterErrors] = useState({});
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpRefs = useRef([]);
  const [step, setStep] = useState(1);
  const [activityDomains, setActivityDomains] = useState(ACTIVITY_DOMAINS);
  const [transportDomains, setTransportDomains] = useState(TRANSPORT_DOMAINS);

  const getErrorMessage = (err, field = null, fallback = 'Une erreur est survenue.') => {
    const data = err?.response?.data || {};
    const detail = data?.detail;
    const nestedErrors = data?.errors;

    const pickFrom = (container) => {
      if (!field || !container || !Object.prototype.hasOwnProperty.call(container, field)) {
        return null;
      }
      const v = container[field];
      if (Array.isArray(v)) return v[0] || null;
      if (typeof v === 'string') return v || null;
      return null;
    };

    const fromNested = pickFrom(nestedErrors);
    if (fromNested) return fromNested;

    // DRF renvoie souvent { phone_number: ["..."] } à la racine, pas sous `errors`
    const fromRoot = pickFrom(data);
    if (fromRoot) return fromRoot;

    if (typeof detail === 'string' && detail) return detail;
    return fallback;
  };

  // Rediriger si déjà authentifié
  useEffect(() => {
    if (isAuthenticated && user) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, user, navigate, location]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Synchroniser l'onglet avec l'URL
  useEffect(() => {
    const tab = searchParams.get('tab') || 'login';
    setCurrentTab(tab);
  }, [searchParams]);

  // Restaurer « numéro vérifié » depuis localStorage (même TTL ~10 min que le backend)
  useEffect(() => {
    if (currentTab !== 'register') return;
    const phone = (registerData.phone_number || '').trim();
    if (!phone) {
      setIsPhoneVerified(false);
      return;
    }
    const n = normalizePhoneForStorage(phone);
    if (!n) {
      setIsPhoneVerified(false);
      return;
    }
    const stored = loadPhoneVerificationFromStorage();
    if (stored && stored.phoneNormalized === n) {
      setIsPhoneVerified(true);
      setOtpSent(false);
    } else {
      setIsPhoneVerified(false);
    }
  }, [currentTab, registerData.phone_number]);

  // Gestionnaires pour le formulaire de connexion
  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
    if (loginErrors[name]) {
      setLoginErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateLogin = () => {
    const newErrors = {};
    const value = (loginData.email_or_phone || '').trim();

    if (!value) {
      newErrors.email_or_phone = 'L\'email ou le numéro de téléphone est requis';
    } else if (value.includes('@') && !/\S+@\S+\.\S+/.test(value)) {
      newErrors.email_or_phone = 'Email invalide';
    }

    if (!loginData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (loginData.password.length < 6) {
      newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    setLoginErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();

    if (!validateLogin()) {
      return;
    }

    setIsLoginLoading(true);

    try {
      await authAPI.getCsrfToken();
      const response = await authAPI.login({ email_or_phone: loginData.email_or_phone.trim(), password: loginData.password });

      if (!response.data || !response.data.user) {
        throw new Error('Réponse de connexion invalide');
      }

      const userData = response.data.user;
      const redirectRoute = response.data.redirect_route;

      useAuthStore.getState().setUser(userData);

      toast({
        title: 'Connexion réussie',
        description: `Bienvenue ${userData.first_name || 'sur LogiConnect'} !`,
        variant: 'success',
      });

      const targetRoute = location.state?.from?.pathname || redirectRoute;
      navigate(targetRoute, { replace: true });

    } catch (error) {
      console.error('Erreur de connexion:', error);

      const errorData = error.response?.data || {};
      const errorMessage = errorData.detail || 'Une erreur est survenue lors de la connexion';
      const fieldErrors = errorData.errors || {};

      toast({
        title: 'Erreur de connexion',
        description: errorMessage,
        variant: 'destructive',
      });

      setLoginErrors({
        ...loginErrors,
        ...fieldErrors,
        general: errorMessage
      });
    } finally {
      setIsLoginLoading(false);
    }
  };

  // Gestionnaires pour le formulaire d'inscription
  const handleRegisterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRegisterData((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      return next;
    });
    if (registerErrors[name]) {
      setRegisterErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (name === 'phone_number') {
      setOtpSent(false);
      setOtpCode('');
      setResendCooldown(0);
    }
  };

  const handleSelectChange = (name, value) => {
    setRegisterData(prev => ({ ...prev, [name]: value }));
    if (registerErrors[name]) {
      setRegisterErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddCustomDomain = (newDomain) => {
    if (!activityDomains.includes(newDomain)) {
      setActivityDomains(prev => [...prev, newDomain]);
    }
  };

  const handleAddCustomTransportDomain = (newDomain) => {
    if (!transportDomains.includes(newDomain)) {
      setTransportDomains(prev => [...prev, newDomain]);
    }
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (!registerData.first_name) newErrors.first_name = 'Le prénom est requis';
    if (registerData.email && !/\S+@\S+\.\S+/.test(registerData.email)) {
      newErrors.email = 'Email invalide';
    }
    if (!registerData.phone_number) newErrors.phone_number = 'Le téléphone est requis';
    if (!isPhoneVerified) newErrors.phone_verified = 'Veuillez vérifier votre numéro de téléphone.';
    if (!registerData.role) newErrors.role = 'Le type de compte est requis';

    setRegisterErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (registerData.role === 'CLIENT') {
      if (!registerData.type_client) {
        newErrors.type_client = 'Le type de client est requis';
      }
      if (registerData.type_client === 'entreprise') {
        if (!registerData.company_name) {
          newErrors.company_name = 'Le nom de l\'entreprise est requis';
        }
        if (!registerData.domaine_activites || registerData.domaine_activites.length === 0) {
          newErrors.domaine_activites = 'Au moins un domaine d\'activité est requis';
        }
      }
    } else if (registerData.role === 'TRANSPORTEUR') {
      if (registerData.is_company && !registerData.organisation_name) {
        newErrors.organisation_name = 'Le nom de l\'organisation est requis';
      }
      if (!registerData.transport_domains || registerData.transport_domains.length === 0) {
        newErrors.transport_domains = 'Au moins un domaine de transport est requis';
      }
    }

    setRegisterErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};

    if (!registerData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (registerData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (!registerData.confirmPassword) {
      newErrors.confirmPassword = 'Veuillez confirmer le mot de passe';
    } else if (registerData.password !== registerData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    setRegisterErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOtp = async () => {
    const phone = (registerData.phone_number || '').trim();
    if (!phone || phone.length < 8) {
      setRegisterErrors({ phone_number: 'Veuillez entrer un numéro de téléphone valide.' });
      return;
    }
    const phoneNorm = normalizePhoneForStorage(phone);
    const stored = loadPhoneVerificationFromStorage();
    if (
      phoneNorm
      && stored
      && stored.phoneNormalized === phoneNorm
      && stored.expiresAt > Date.now()
    ) {
      setIsPhoneVerified(true);
      setOtpSent(false);
      setOtpCode('');
      toast({
        title: 'Numéro déjà vérifié',
        description: 'Vous pouvez continuer l\'inscription.',
        variant: 'success',
      });
      return;
    }
    setRegisterErrors({});
    setIsRegisterLoading(true);
    try {
      await authAPI.getCsrfToken();
      await authAPI.sendOtp({ phone_number: phone });
      setOtpSent(true);
      setOtpCode('');
      setResendCooldown(300);
      toast({ title: 'Code envoyé', description: 'Vérifiez vos SMS.', variant: 'success' });
      setTimeout(() => {
        const first = otpRefs.current?.[0];
        if (first && typeof first.focus === 'function') first.focus();
      }, 0);
    } catch (err) {
      const msg = getErrorMessage(
        err,
        'phone_number',
        'Impossible d\'envoyer le code. Veuillez essayer avec un autre numéro.',
      );
      setRegisterErrors({ phone_number: msg });
      toast({ title: 'Erreur', description: msg, variant: 'destructive' });
    } finally {
      setIsRegisterLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const phone = (registerData.phone_number || '').trim();
    const code = (otpCode || '').trim();
    if (!phone || !code || code.length !== 6) {
      setRegisterErrors({ code: 'Veuillez entrer le code à 6 chiffres reçu par SMS.' });
      return;
    }
    setRegisterErrors({});
    setIsRegisterLoading(true);
    try {
      await authAPI.getCsrfToken();
      await authAPI.verifyOtp({ phone_number: phone, code });
      const phoneNorm = normalizePhoneForStorage(phone);
      savePhoneVerificationToStorage(phoneNorm);
      setIsPhoneVerified(true);
      setOtpSent(false);
      toast({ title: 'Numéro vérifié', description: 'Vous pouvez continuer l\'inscription.', variant: 'success' });
    } catch (err) {
      const msg = getErrorMessage(err, 'code', 'Code incorrect ou expiré.');
      setRegisterErrors({ code: msg });
      toast({ title: 'Erreur', description: msg, variant: 'destructive' });
    } finally {
      setIsRegisterLoading(false);
    }
  };

  const setOtpAtIndex = (idx, digit) => {
    setOtpCode((prev) => {
      const normalized = (prev || '').replace(/\D/g, '').slice(0, 6);
      const chars = normalized.padEnd(6, '').split('');
      chars[idx] = (digit || '').replace(/\D/g, '').slice(0, 1);
      return chars.join('').replace(/\s/g, '');
    });
  };

  const handleOtpChange = (idx, value) => {
    const digit = (value || '').replace(/\D/g, '').slice(0, 1);
    setOtpAtIndex(idx, digit);
    if (digit && otpRefs.current?.[idx + 1]) {
      otpRefs.current[idx + 1].focus();
    }
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace') {
      const current = (otpCode || '')[idx] || '';
      if (!current && otpRefs.current?.[idx - 1]) {
        otpRefs.current[idx - 1].focus();
      }
      // Laisser l'input gérer la suppression, et synchroniser ensuite via onChange
    } else if (e.key === 'ArrowLeft' && otpRefs.current?.[idx - 1]) {
      e.preventDefault();
      otpRefs.current[idx - 1].focus();
    } else if (e.key === 'ArrowRight' && otpRefs.current?.[idx + 1]) {
      e.preventDefault();
      otpRefs.current[idx + 1].focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    setOtpCode(pasted);
    const nextIndex = Math.min(pasted.length, 5);
    const next = otpRefs.current?.[nextIndex];
    if (next && typeof next.focus === 'function') next.focus();
  };

  const handleNextStep = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep3()) return;

    setIsRegisterLoading(true);

    try {
      const data = {
        password: registerData.password,
        first_name: registerData.first_name,
        last_name: registerData.last_name,
        phone_number: registerData.phone_number,
        role: registerData.role,
      };
      if (registerData.email?.trim()) data.email = registerData.email.trim();

      const profile = {};

      if (registerData.role === 'CLIENT') {
        profile.type_client = registerData.type_client;
        if (registerData.type_client === 'entreprise') {
          profile.company_name = registerData.company_name;
          profile.domaine_activites = registerData.domaine_activites;
        }
        if (registerData.address) profile.address = registerData.address;
      } else if (registerData.role === 'TRANSPORTEUR') {
        profile.is_company = registerData.is_company;
        if (registerData.is_company) {
          profile.organisation_name = registerData.organisation_name;
          if (registerData.registration_number) profile.registration_number = registerData.registration_number;
          if (registerData.tax_info) profile.tax_info = registerData.tax_info;
        }
        profile.transport_domains = registerData.transport_domains;
      }

      if (Object.keys(profile).length > 0) {
        data.profile = profile;
      }

      await authAPI.register(data);

      toast({
        title: 'Inscription réussie !',
        description: 'Votre compte a été créé avec succès. Un administrateur doit verifier votre compte avant que vous puissiez vous connecter.',
        variant: 'success',
      });

      // Réinitialiser le formulaire d'inscription
      setRegisterData({
        email: '',
        password: '',
        confirmPassword: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        role: 'CLIENT',
        type_client: '',
        company_name: '',
        domaine_activites: [],
        address: '',
        is_company: false,
        organisation_name: '',
        registration_number: '',
        tax_info: '',
        transport_domains: [],
      });
      setRegisterErrors({});
      setShowRegisterPassword(false);
      setShowConfirmPassword(false);
      setStep(1);
      setOtpSent(false);
      setOtpCode('');
      setIsPhoneVerified(false);
      setResendCooldown(0);
      clearPhoneVerificationStorage();

      // Basculer vers l'onglet de connexion
      setSearchParams({ tab: 'login' });
      setCurrentTab('login');

    } catch (error) {
      console.error('Register error:', error);

      const errorMessage = error.response?.data?.email?.[0]
        || error.response?.data?.detail
        || error.response?.data?.message
        || 'Une erreur est survenue lors de l\'inscription';

      toast({
        title: 'Erreur d\'inscription',
        description: errorMessage,
        variant: 'destructive',
      });

      setRegisterErrors({ general: errorMessage });
      setStep(1);
    } finally {
      setIsRegisterLoading(false);
    }
  };

  const handleTabChange = (value) => {
    setCurrentTab(value);
    setSearchParams({ tab: value });
    // Réinitialiser les erreurs et l'étape lors du changement d'onglet
    setLoginErrors({});
    setRegisterErrors({});
    setStep(1);
    setOtpSent(false);
    setOtpCode('');
    setIsPhoneVerified(false);
    setResendCooldown(0);
  };

  const roleOptions = [
    { value: 'CLIENT', label: 'Client', description: 'Je veux expédier des marchandises' },
    { value: 'TRANSPORTEUR', label: 'Transporteur', description: 'Je veux transporter des marchandises' },
  ];

  return (
    <>
      <Helmet>
        <title>Authentification - LogiConnect</title>
      </Helmet>

      <AuthLayout>
        <div className="w-full">
          {/* Bouton de retour à l'accueil */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 mb-6 text-sm font-medium text-white bg-[#0d5850] hover:bg-[#0a4a42] rounded-lg transition-colors shadow-sm hover:shadow-md"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>

          {/* Onglets - Fixes en haut */}
          <div className="mb-6">
            <Tabs value={currentTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-lg h-auto">
                <TabsTrigger
                  value="login"
                  className={`px-6 py-3 text-sm font-semibold rounded-md transition-all ${
                    currentTab === 'login'
                      ? 'bg-white text-[#0d5850] shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Se Connecter
                </TabsTrigger>
                <TabsTrigger
                  value="register"
                  className={`px-6 py-3 text-sm font-semibold rounded-md transition-all ${
                    currentTab === 'register'
                      ? 'bg-white text-amber-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  S'inscrire
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Contenu avec effet rideau */}
          <div className="relative border border-gray-200 rounded-xl bg-white shadow-lg overflow-hidden">
            <Tabs value={currentTab} onValueChange={handleTabChange} className="w-full">

            {/* Onglet Connexion */}
            <TabsContent value="login" className="mt-0 p-6 lg:p-8">
              <AnimatePresence mode="wait">
                {currentTab === 'login' && (
                  <motion.div
                    key="login"
                    initial={{ opacity: 0, x: -100, clipPath: 'inset(0 100% 0 0)' }}
                    animate={{ opacity: 1, x: 0, clipPath: 'inset(0 0% 0 0)' }}
                    exit={{ opacity: 0, x: 100, clipPath: 'inset(0 0% 0 100%)' }}
                    transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                    className="space-y-6"
                  >
                    <div className="mb-8 border-l-4 border-[#0d5850] pl-4">
                      <h1 className="text-3xl font-bold text-gray-900 mb-2">Bienvenue !</h1>
                      <p className="text-gray-600">Connectez-vous pour accéder à votre espace</p>
                    </div>

                    {loginErrors.general && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3"
                      >
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-600">{loginErrors.general}</p>
                      </motion.div>
                    )}

                    <form onSubmit={handleLoginSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="login-email-or-phone" className="text-sm font-medium text-gray-700">
                    Email ou numéro de téléphone
                  </Label>
                  <div className="relative">
                    <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="login-email-or-phone"
                      name="email_or_phone"
                      type="text"
                      inputMode="email"
                      autoComplete="username"
                      placeholder="Email ou numéro de téléphone"
                      value={loginData.email_or_phone}
                      onChange={handleLoginChange}
                      className={`pr-10 h-12 bg-white text-gray-900 placeholder:text-gray-400 border-gray-300 focus:border-[#0d5850] focus:ring-[#0d5850] ${loginErrors.email_or_phone ? 'border-red-500' : ''}`}
                      disabled={isLoginLoading}
                    />
                  </div>
                  {loginErrors.email_or_phone && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {loginErrors.email_or_phone}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-sm font-medium text-gray-700">
                    Mot de passe
                  </Label>
                  <div className="relative">
                    <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="login-password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={handleLoginChange}
                      className={`pr-10 h-12 bg-white text-gray-900 placeholder:text-gray-400 border-gray-300 focus:border-[#0d5850] focus:ring-[#0d5850] ${loginErrors.password ? 'border-red-500' : ''}`}
                      disabled={isLoginLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {loginErrors.password && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {loginErrors.password}
                    </p>
                  )}
                </div>

                <div className="flex justify-end">
                  <a
                    href="/auth/forgot-password"
                    className="text-sm text-[#0d5850] hover:underline"
                  >
                    Mot de passe oublié ?
                  </a>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base font-semibold bg-[#0d5850] hover:bg-[#0a4a42] text-white"
                  disabled={isLoginLoading}
                >
                  {isLoginLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Connexion en cours...
                    </span>
                  ) : (
                    'Connexion'
                  )}
                </Button>
              </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </TabsContent>

            {/* Onglet Inscription */}
            <TabsContent value="register" className="mt-0 p-6 lg:p-8 bg-gradient-to-br from-amber-50/50 to-white">
              <AnimatePresence mode="wait">
                {currentTab === 'register' && (
                  <motion.div
                    key="register"
                    initial={{ opacity: 0, x: 100, clipPath: 'inset(0 0% 0 100%)' }}
                    animate={{ opacity: 1, x: 0, clipPath: 'inset(0 0% 0 0%)' }}
                    exit={{ opacity: 0, x: -100, clipPath: 'inset(0 100% 0 0)' }}
                    transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                    className="space-y-6"
                  >
                    <div className="mb-8 border-l-4 border-amber-500 pl-4">
                      <h1 className="text-3xl font-bold text-gray-900 mb-2">Créer un compte</h1>
                      <p className="text-gray-600 mb-4">Rejoignez LogiConnect dès aujourd'hui</p>

                      <div className="flex items-center gap-2 mb-2">
                        <div className={`h-2 flex-1 rounded-full transition-colors ${step >= 1 ? 'bg-amber-500' : 'bg-gray-200'}`} />
                        <div className={`h-2 flex-1 rounded-full transition-colors ${step >= 2 ? 'bg-amber-500' : 'bg-gray-200'}`} />
                        <div className={`h-2 flex-1 rounded-full transition-colors ${step >= 3 ? 'bg-amber-500' : 'bg-gray-200'}`} />
                      </div>
                      <p className="text-xs text-gray-500">{`Étape ${step} sur 3`}</p>
                    </div>

                    {registerErrors.general && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3"
                      >
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-600">{registerErrors.general}</p>
                      </motion.div>
                    )}

                    <div className="max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
                      <form onSubmit={step < 3 ? (e) => { e.preventDefault(); handleNextStep(); } : handleRegisterSubmit} className="space-y-5">
                  {/* ÉTAPE 1: Informations de base */}
                  {step === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="space-y-5"
                    >
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="first_name" className="text-sm font-medium text-gray-700">
                            Prénom <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="first_name"
                            name="first_name"
                            placeholder="Votre prénom"
                            value={registerData.first_name}
                            onChange={handleRegisterChange}
                            className={`h-12 bg-white text-gray-900 placeholder:text-gray-400 border-gray-300 focus:border-amber-500 focus:ring-amber-500 ${registerErrors.first_name ? 'border-red-500' : ''}`}
                          />
                          {registerErrors.first_name && <p className="text-xs text-red-600">{registerErrors.first_name}</p>}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="last_name" className="text-sm font-medium text-gray-700">
                            Nom
                          </Label>
                          <Input
                            id="last_name"
                            name="last_name"
                            placeholder="Votre nom"
                            value={registerData.last_name}
                            onChange={handleRegisterChange}
                            className="h-12 bg-white text-gray-900 placeholder:text-gray-400 border-gray-300 focus:border-amber-500 focus:ring-amber-500"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone_number" className="text-sm font-medium text-gray-700">
                          Téléphone <span className="text-red-500">*</span>
                        </Label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <Input
                              id="phone_number"
                              name="phone_number"
                              type="tel"
                              placeholder="+224 627 61 38 35"
                              value={registerData.phone_number}
                              onChange={handleRegisterChange}
                              className={`pr-10 h-12 bg-white text-gray-900 placeholder:text-gray-400 border-gray-300 focus:border-amber-500 focus:ring-amber-500 ${registerErrors.phone_number ? 'border-red-500' : ''}`}
                              disabled={isRegisterLoading}
                            />
                          </div>
                          <Button
                            type="button"
                            onClick={handleSendOtp}
                            disabled={isRegisterLoading || !registerData.phone_number?.trim() || isPhoneVerified}
                            className="h-12 bg-amber-600 hover:bg-amber-700 text-white"
                          >
                            {isPhoneVerified ? 'Vérifié' : 'Vérifier le numéro'}
                          </Button>
                        </div>
                        {isPhoneVerified && (
                          <div className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                            Numero verifie
                          </div>
                        )}
                        {registerErrors.phone_number && <p className="text-xs text-red-600">{registerErrors.phone_number}</p>}
                        {!registerErrors.phone_number && registerErrors.phone_verified && (
                          <p className="text-xs text-red-600">{registerErrors.phone_verified}</p>
                        )}

                        {otpSent && !isPhoneVerified && (
                          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 p-4">
                            <div className="mb-2 flex items-center justify-between gap-3">
                              <Label htmlFor="otp_code" className="text-sm font-medium text-gray-800">
                                Code reçu par SMS
                              </Label>
                              <span className="text-xs text-gray-600">
                                {resendCooldown > 0 ? `Expire/renvoi: ${Math.ceil(resendCooldown / 60)} min` : 'Vous pouvez renvoyer le code'}
                              </span>
                            </div>

                            <div className="flex flex-col gap-3">
                              <div className="w-full max-w-md" onPaste={handleOtpPaste}>
                                <div className="flex items-center justify-start gap-2">
                                  {Array.from({ length: 6 }).map((_, idx) => (
                                    <input
                                      key={idx}
                                      ref={(el) => { otpRefs.current[idx] = el; }}
                                      value={(otpCode || '').replace(/\D/g, '').padEnd(6, '')[idx] || ''}
                                      onChange={(e) => {
                                        if (registerErrors.code) setRegisterErrors((p) => ({ ...p, code: '' }));
                                        handleOtpChange(idx, e.target.value);
                                      }}
                                      onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                                      inputMode="numeric"
                                      autoComplete="one-time-code"
                                      maxLength={1}
                                      disabled={isRegisterLoading}
                                      className={`h-12 w-12 rounded-md border bg-white text-center text-lg font-semibold text-gray-900 outline-none transition-colors focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30 ${
                                        registerErrors.code ? 'border-red-500' : 'border-gray-300'
                                      } ${isRegisterLoading ? 'opacity-60' : ''}`}
                                    />
                                  ))}
                                </div>
                              </div>

                              <Button
                                type="button"
                                onClick={handleVerifyOtp}
                                disabled={isRegisterLoading || otpCode.length !== 6}
                                className="h-12 w-full max-w-md bg-amber-600 hover:bg-amber-700 text-white"
                              >
                                Valider
                              </Button>
                            </div>
                            {registerErrors.code && <p className="text-xs text-red-600">{registerErrors.code}</p>}

                            <div className="mt-2 flex items-center justify-between">
                              <p className="text-xs text-gray-500">Vous n’avez rien reçu ?</p>
                              <button
                                type="button"
                                onClick={handleSendOtp}
                                disabled={isRegisterLoading || resendCooldown > 0}
                                className="text-xs font-medium text-amber-700 hover:text-amber-800 hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
                              >
                                {resendCooldown > 0 ? `Renvoyer dans ${Math.ceil(resendCooldown / 60)} min` : 'Renvoyer le code'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {isPhoneVerified && (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="register-email" className="text-sm font-medium text-gray-700">
                              Email (optionnel)
                            </Label>
                            <div className="relative">
                              <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <Input
                                id="register-email"
                                name="email"
                                type="email"
                                placeholder="exemple@gmail.com"
                                value={registerData.email}
                                onChange={handleRegisterChange}
                                className={`pr-10 h-12 bg-white text-gray-900 placeholder:text-gray-400 border-gray-300 focus:border-amber-500 focus:ring-amber-500 ${registerErrors.email ? 'border-red-500' : ''}`}
                              />
                            </div>
                            {registerErrors.email && <p className="text-xs text-red-600">{registerErrors.email}</p>}
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-gray-700">
                              Type de compte <span className="text-red-500">*</span>
                            </Label>
                            <div className="grid grid-cols-1 gap-3">
                              {roleOptions.map((option) => (
                                <label
                                  key={option.value}
                                  className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                    registerData.role === option.value
                                      ? 'border-amber-500 bg-amber-50'
                                      : 'border-gray-300 hover:border-amber-500/50'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name="role"
                                    value={option.value}
                                    checked={registerData.role === option.value}
                                    onChange={(e) => handleSelectChange('role', e.target.value)}
                                    className="mt-1"
                                  />
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">{option.label}</div>
                                    <div className="text-sm text-gray-600">{option.description}</div>
                                  </div>
                                  {registerData.role === option.value && (
                                    <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0" />
                                  )}
                                </label>
                              ))}
                            </div>
                          </div>
                        </>
                      )}

                      <Button
                        type="submit"
                        disabled={!isPhoneVerified}
                        className="w-full h-12 text-base font-semibold bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-60 disabled:hover:bg-amber-500"
                      >
                        Continuer <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </motion.div>
                  )}

                  {/* ÉTAPE 2: Informations de profil */}
                  {step === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="space-y-5"
                    >
                      {registerData.role === 'CLIENT' && (
                        <>
                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-gray-700">
                              Type de client <span className="text-red-500">*</span>
                            </Label>
                            <Select value={registerData.type_client} onValueChange={(value) => handleSelectChange('type_client', value)}>
                              <SelectTrigger className={`h-12 bg-white border-gray-300 text-gray-900 focus:border-amber-500 focus:ring-amber-500 ${registerErrors.type_client ? 'border-red-500' : ''}`}>
                                <SelectValue placeholder="Sélectionner le type" className="text-gray-900" />
                              </SelectTrigger>
                              <SelectContent className="bg-white text-gray-900 border-gray-200">
                                <SelectItem value="particulier" className="text-gray-900 focus:bg-amber-50 focus:text-amber-900 cursor-pointer">
                                  Particulier
                                </SelectItem>
                                <SelectItem value="entreprise" className="text-gray-900 focus:bg-amber-50 focus:text-amber-900 cursor-pointer">
                                  Entreprise
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            {registerErrors.type_client && <p className="text-xs text-red-600">{registerErrors.type_client}</p>}
                          </div>

                          {registerData.type_client === 'entreprise' && (
                            <>
                              <div className="space-y-2">
                                <Label htmlFor="company_name" className="text-sm font-medium text-gray-700">
                                  Nom de l'entreprise <span className="text-red-500">*</span>
                                </Label>
                                <div className="relative">
                                  <Building className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                  <Input
                                    id="company_name"
                                    name="company_name"
                                    placeholder="Ex: LogiTrans SARL"
                                    value={registerData.company_name}
                                    onChange={handleRegisterChange}
                                    className={`pr-10 h-12 bg-white text-gray-900 border-gray-300 focus:border-amber-500 focus:ring-amber-500 ${registerErrors.company_name ? 'border-red-500' : ''}`}
                                  />
                                </div>
                                {registerErrors.company_name && <p className="text-xs text-red-600">{registerErrors.company_name}</p>}
                              </div>

                              <div className="space-y-2">
                                <Label className="text-sm font-medium text-gray-700">
                                  Domaines d'activité <span className="text-red-500">*</span>
                                </Label>
                                <MultiSelect
                                  options={activityDomains}
                                  value={registerData.domaine_activites}
                                  onChange={(value) => handleSelectChange('domaine_activites', value)}
                                  placeholder="Sélectionner les domaines..."
                                  allowCustom={true}
                                  onAddCustom={handleAddCustomDomain}
                                  className={registerErrors.domaine_activites ? 'border-red-500' : ''}
                                />
                                {registerErrors.domaine_activites && <p className="text-xs text-red-600">{registerErrors.domaine_activites}</p>}
                              </div>
                            </>
                          )}

                          <div className="space-y-2">
                            <Label htmlFor="address" className="text-sm font-medium text-gray-700">
                              Adresse
                            </Label>
                            <Input
                              id="address"
                              name="address"
                              placeholder="Adresse complète"
                              value={registerData.address}
                              onChange={handleRegisterChange}
                              className="h-12 bg-white text-gray-900 border-gray-300 focus:border-amber-500 focus:ring-amber-500"
                            />
                          </div>
                        </>
                      )}

                      {registerData.role === 'TRANSPORTEUR' && (
                        <>
                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-gray-700">
                              Type de transporteur
                            </Label>
                            <Select value={registerData.is_company ? 'true' : 'false'} onValueChange={(value) => handleSelectChange('is_company', value === 'true')}>
                              <SelectTrigger className="h-12 bg-white border-gray-300 text-gray-900 focus:border-amber-500 focus:ring-amber-500">
                                <SelectValue placeholder="Sélectionner le type" className="text-gray-900" />
                              </SelectTrigger>
                              <SelectContent className="bg-white text-gray-900 border-gray-200">
                                <SelectItem value="false" className="text-gray-900 focus:bg-amber-50 focus:text-amber-900 cursor-pointer">
                                  Indépendant
                                </SelectItem>
                                <SelectItem value="true" className="text-gray-900 focus:bg-amber-50 focus:text-amber-900 cursor-pointer">
                                  Entreprise
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-gray-700">
                              Domaines de transport <span className="text-red-500">*</span>
                            </Label>
                            <MultiSelect
                              options={transportDomains}
                              value={registerData.transport_domains}
                              onChange={(value) => handleSelectChange('transport_domains', value)}
                              placeholder="Sélectionner vos domaines de transport..."
                              allowCustom={true}
                              onAddCustom={handleAddCustomTransportDomain}
                              className={registerErrors.transport_domains ? 'border-red-500' : ''}
                            />
                            {registerErrors.transport_domains && <p className="text-xs text-red-600">{registerErrors.transport_domains}</p>}
                            <p className="text-xs text-gray-500">
                              Sélectionnez tous les types de transport que vous proposez
                            </p>
                          </div>

                          {registerData.is_company && (
                            <>
                              <div className="space-y-2">
                                <Label htmlFor="organisation_name" className="text-sm font-medium text-gray-700">
                                  Nom de l'organisation <span className="text-red-500">*</span>
                                </Label>
                                <div className="relative">
                                  <Building className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                  <Input
                                    id="organisation_name"
                                    name="organisation_name"
                                    placeholder="Ex: Transports Rapides SARL"
                                    value={registerData.organisation_name}
                                    onChange={handleRegisterChange}
                                    className={`pr-10 h-12 bg-white text-gray-900 border-gray-300 focus:border-amber-500 focus:ring-amber-500 ${registerErrors.organisation_name ? 'border-red-500' : ''}`}
                                  />
                                </div>
                                {registerErrors.organisation_name && <p className="text-xs text-red-600">{registerErrors.organisation_name}</p>}
                              </div>

                              <div className="space-y-2">
                                <Label htmlFor="registration_number" className="text-sm font-medium text-gray-700">
                                  N° d'enregistrement
                                </Label>
                                <Input
                                  id="registration_number"
                                  name="registration_number"
                                  placeholder="RCCM/GN..."
                                  value={registerData.registration_number}
                                  onChange={handleRegisterChange}
                                  className="h-12 bg-white text-gray-900 border-gray-300 focus:border-amber-500 focus:ring-amber-500"
                                />
                              </div>

                              <div className="space-y-2">
                                <Label htmlFor="tax_info" className="text-sm font-medium text-gray-700">
                                  Numéro fiscal (NIF)
                                </Label>
                                <Input
                                  id="tax_info"
                                  name="tax_info"
                                  placeholder="NIF..."
                                  value={registerData.tax_info}
                                  onChange={handleRegisterChange}
                                  className="h-12 bg-white text-gray-900 border-gray-300 focus:border-amber-500 focus:ring-amber-500"
                                />
                              </div>
                            </>
                          )}
                        </>
                      )}

                      <div className="flex gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setStep(1)}
                          className="w-1/3 h-12 border-gray-300"
                        >
                          Retour
                        </Button>
                        <Button
                          type="submit"
                          className="flex-1 h-12 text-base font-semibold bg-amber-500 hover:bg-amber-600 text-white"
                        >
                          Continuer <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                      </div>
                    </motion.div>
                  )}

                  {/* ÉTAPE 3: Mot de passe */}
                  {step === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="space-y-5"
                    >
                      <div className="space-y-2">
                        <Label htmlFor="register-password" className="text-sm font-medium text-gray-700">
                          Mot de passe <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative">
                          <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <Input
                            id="register-password"
                            name="password"
                            type={showRegisterPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={registerData.password}
                            onChange={handleRegisterChange}
                            className={`pr-10 h-12 bg-white text-gray-900 border-gray-300 focus:border-amber-500 focus:ring-amber-500 ${registerErrors.password ? 'border-red-500' : ''}`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                            className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {showRegisterPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                        {registerErrors.password && <p className="text-xs text-red-600">{registerErrors.password}</p>}
                        <p className="text-xs text-gray-500">Minimum 8 caractères</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                          Confirmer le mot de passe <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative">
                          <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={registerData.confirmPassword}
                            onChange={handleRegisterChange}
                            className={`pr-10 h-12 bg-white text-gray-900 border-gray-300 focus:border-amber-500 focus:ring-amber-500 ${registerErrors.confirmPassword ? 'border-red-500' : ''}`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                        {registerErrors.confirmPassword && <p className="text-xs text-red-600">{registerErrors.confirmPassword}</p>}
                      </div>

                      <div className="flex gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setStep(2)}
                          className="w-1/3 h-12 border-gray-300"
                        >
                          Retour
                        </Button>
                        <Button
                          type="submit"
                          disabled={isRegisterLoading}
                          className="flex-1 h-12 text-base font-semibold bg-amber-500 hover:bg-amber-600 text-white"
                        >
                          {isRegisterLoading ? (
                            <span className="flex items-center gap-2">
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Création...
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              Créer mon compte <ArrowRight className="w-5 h-5" />
                            </span>
                          )}
                        </Button>
                      </div>
                    </motion.div>
                  )}
                      </form>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </TabsContent>
          </Tabs>
          </div>
        </div>
      </AuthLayout>
    </>
  );
};

export default Auth;
